import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  Code,
  EmptyState,
  GlassButton,
  LoadingOverlay,
} from 'dashboard/components/common';
import { brandCnameTarget as BRAND_CNAME_TARGET } from 'config';
import SubdomainRuleDialog from './SubdomainRuleDialog';

const TARGET_LABEL = {
  webapp: 'Full storefront',
  page: 'Specific page',
  dashboard: 'Your dashboard',
  coming_soon: 'Coming soon',
};

const TARGET_COLOR = {
  webapp: 'primary',
  page: 'secondary',
  dashboard: 'secondary',
  coming_soon: 'default',
};

/**
 * Routing rules for subdomains under the brand's primary domain. The
 * resolver falls back to "Coming soon" for any subdomain that doesn't
 * have an explicit rule, so brand owners don't need to insert one to
 * start serving traffic.
 */
const copyText = (text) => {
  try {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  } catch (_) {
    toast.error('Copy failed');
  }
};

export default function SubdomainRulesCard({ brandId, primary }) {
  const [rules, setRules] = useState([]);
  const [pages, setPages] = useState({}); // _id → page (for showing the page title in the table)
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const apex = primary?.domain;
  const enabled = primary?.status === 'verified';

  const load = useCallback(() => {
    if (!brandId || !enabled) return Promise.resolve();
    setLoading(true);
    return Promise.all([
      API.listBrandSubdomains({ brand_id: brandId }),
      // Pull page list once so we can show titles for `target_type=page`
      // rows without per-row lookups. Best-effort: failure here just
      // means we show the page id instead of the title.
      API.listSitePages({ brand_id: brandId }).catch(() => []),
    ])
      .then(([rs, pgs]) => {
        setRules(Array.isArray(rs) ? rs : []);
        const pageMap = {};
        (Array.isArray(pgs) ? pgs : []).forEach((p) => { pageMap[p._id] = p; });
        setPages(pageMap);
      })
      .catch((err) => toast.error(err?.message || 'Failed to load subdomain rules', { toastId: 'sub-rules-load' }))
      .finally(() => setLoading(false));
  }, [brandId, enabled]);

  useEffect(() => { load(); }, [load]);

  const remove = (rule) => {
    const host = rule.subdomain ? `${rule.subdomain}.${apex}` : apex;
    if (!window.confirm(`Remove the rule for ${host}? It will fall back to "Coming soon".`)) return;
    API.deleteBrandSubdomain({ brand_id: brandId, _id: rule._id })
      .then(() => { toast.success('Removed'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed to remove'));
  };

  const toggle = (rule) => {
    API.updateBrandSubdomain({
      brand_id: brandId,
      _id: rule._id,
      enabled: !rule.enabled,
    })
      .then(() => load())
      .catch((err) => toast.error(err?.message || 'Failed to toggle'));
  };

  return (
    <Box>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        flexWrap='wrap'
        gap='10px'
        mb='12px'
      >
        <Box>
          <Typography variant='h6' fontWeight='700'>Subdomain routing</Typography>
          <Typography variant='body2' color='text.secondary'>
            Map each subdomain (and the bare apex) to either the full storefront, a single page from the page builder, or a "Coming soon" placeholder. Unmapped subdomains default to Coming soon.
          </Typography>
        </Box>
        {/* The reason is also spelled out below, but not on a phone where
            it may be off the bottom of the screen — and a tooltip is the only
            channel that works on touch, where there is no hover. */}
        <GlassButton
          variant='contained'
          startIcon={<AddIcon />}
          disabled={!enabled}
          disabledReason='Add and verify your primary domain above first — a rule needs a verified domain to route.'
          onClick={() => { setEditing(null); setOpen(true); }}
        >
          New rule
        </GlassButton>
      </Stack>

      {!enabled && (
        <EmptyState>
          Add and verify your primary domain above before mapping subdomains.
        </EmptyState>
      )}

      {enabled && (
        <LoadingOverlay loading={loading && rules.length === 0} minHeight={160}>
          <TableContainer
            component={Box}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflowX: 'auto' }}
          >
            <Table size='small'>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell>Hostname</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <EmptyState>
                        No subdomain rules yet. Every hostname under <Code>{apex}</Code> currently falls back to "Coming soon" — click <strong>New rule</strong> to point one at a page or the storefront.
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                )}
                {rules.map((r) => {
                  const host = r.subdomain ? `${r.subdomain}.${apex}` : apex;
                  const page = r.target_type === 'page' ? pages[r.target_page_id] : null;
                  return (
                    <TableRow key={r._id}>
                      <TableCell>
                        <Typography variant='body2' fontWeight='600'>
                          {host}
                        </Typography>
                        {/* The DNS record to publish for THIS hostname. It used to
                            be one generic "point www, shop… at cdn" line on the
                            primary-domain card, which meant reading an example and
                            translating it. Here the exact record is spelled out
                            per rule, and the apex is called out separately because
                            a bare domain usually cannot take a CNAME. */}
                        {!r.subdomain ? (
                          <Typography variant='caption' color='text.secondary' display='block'>
                            apex — most registrars cannot CNAME a bare domain; use an
                            ALIAS/ANAME record to <Code>{BRAND_CNAME_TARGET}</Code>, or
                            your provider's redirect to a subdomain.
                          </Typography>
                        ) : (
                          <Stack direction='row' spacing='6px' alignItems='center' mt='4px' flexWrap='wrap'>
                            <Typography variant='caption' color='text.secondary'>
                              CNAME <Code>{r.subdomain}</Code> →
                            </Typography>
                            <Code>{BRAND_CNAME_TARGET}</Code>
                            <IconButton size='small' onClick={() => copyText(BRAND_CNAME_TARGET)}>
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction='row' spacing='6px' alignItems='center'>
                          <Chip
                            size='small'
                            color={TARGET_COLOR[r.target_type] || 'default'}
                            label={TARGET_LABEL[r.target_type] || r.target_type}
                          />
                          {r.target_type === 'page' && (
                            <Typography variant='caption' color='text.secondary'>
                              → {page?.title || page?.slug || r.target_page_id}
                            </Typography>
                          )}
                        </Stack>
                        {r.note && (
                          <Typography variant='caption' color='text.secondary' display='block' mt='2px'>
                            {r.note}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          size='small'
                          checked={!!r.enabled}
                          onChange={() => toggle(r)}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Edit'>
                          <IconButton
                            size='small'
                            onClick={() => { setEditing(r); setOpen(true); }}
                          >
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton size='small' color='error' onClick={() => remove(r)}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </LoadingOverlay>
      )}

      <SubdomainRuleDialog
        open={open}
        onClose={() => setOpen(false)}
        brandId={brandId}
        apex={apex}
        editing={editing}
        onSaved={load}
      />
    </Box>
  );
}
